import {
  Button,
  Field,
  FieldError,
  FieldLabel,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@/components/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import type * as fhirTypes from "fhir/r4";
import { useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import type { z } from "zod";
import { useReferProviderCodes } from "../network/useProviderCodes";
import type { AmazonReferOutDetails } from "../network/useRecommendations";
import { useReferReasonCodes } from "../network/useReferReasonCodes";
import { useReferUrgencyCodes } from "../network/useUrgencyCodes";
import { referralSchema } from "./schemas";

interface ReferralEditorProps {
  onSubmit(v: z.infer<typeof referralSchema>): void;
  onCancel(): void;
  isLoading: boolean;
  referOutDetails?: AmazonReferOutDetails;
  recommendationTask?: fhirTypes.Task;
}

export const ReferralEditor = ({
  referOutDetails,
  recommendationTask,
  onCancel,
  onSubmit,
  isLoading,
}: ReferralEditorProps) => {
  const ref = useRef<HTMLFormElement>(null);

  const { data: reasonData } = useReferReasonCodes();
  const { data: urgencyData } = useReferUrgencyCodes();
  const { data: providerData } = useReferProviderCodes();

  const form = useForm<z.infer<typeof referralSchema>>({
    resolver: zodResolver(referralSchema),
    defaultValues: {
      reason: referOutDetails?.referralReasonCode || "",
      provider: referOutDetails?.referralProviderCode || "",
      urgency: referOutDetails?.referralUrgencyCode || "",
      direction: recommendationTask?.description || "",
    },
  });

  return (
    <form
      ref={ref}
      onSubmit={form.handleSubmit(onSubmit)}
      data-testid="edit-refer-out-form"
    >
      <div className="flex flex-col gap-2">
        <Controller
          control={form.control}
          name="reason"
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor="reason">Referral Reason*</FieldLabel>
              <Select
                value={field.value}
                onValueChange={field.onChange}
                disabled={field.disabled}
              >
                <SelectTrigger
                  data-testid="refer-reason-select"
                  id="reason"
                  aria-invalid={fieldState.invalid}
                >
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {reasonData?.reasonCodes.map(({ code, label }) => (
                    <SelectItem key={code} value={code}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FieldError errors={[fieldState.error]} />
            </Field>
          )}
        />

        <Controller
          control={form.control}
          name="provider"
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor="provider">Provider Type*</FieldLabel>
              <Select
                value={field.value}
                onValueChange={field.onChange}
                disabled={field.disabled}
              >
                <SelectTrigger
                  data-testid="refer-provider-select"
                  id="provider"
                  aria-invalid={fieldState.invalid}
                >
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {providerData?.providerCodes.map(({ code, label }) => (
                    <SelectItem key={code} value={code}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FieldError errors={[fieldState.error]} />
            </Field>
          )}
        />

        <Controller
          control={form.control}
          name="urgency"
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor="urgency">Urgency*</FieldLabel>
              <Select
                value={field.value}
                onValueChange={field.onChange}
                disabled={field.disabled}
              >
                <SelectTrigger
                  data-testid="refer-urgency-select"
                  id="urgency"
                  aria-invalid={fieldState.invalid}
                >
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {urgencyData?.urgencyCodes.map(({ code, label }) => (
                    <SelectItem key={code} value={code}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FieldError errors={[fieldState.error]} />
            </Field>
          )}
        />

        <Controller
          control={form.control}
          name="direction"
          render={({ field, fieldState }) => (
            <Field
              className="col-span-2"
              data-invalid={fieldState.invalid}
              data-testid="refer-out-direction"
            >
              <FieldLabel htmlFor="direction">Additional Directions</FieldLabel>
              <Textarea
                {...field}
                id="direction"
                aria-invalid={fieldState.invalid}
              />
              <FieldError errors={[fieldState.error]} />
            </Field>
          )}
        />
      </div>

      <div className="flex gap-2 ml-auto mt-2 w-fit">
        <Button size="xs" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>

        <Button size="xs" type="submit" isLoading={isLoading}>
          Save
        </Button>
      </div>
    </form>
  );
};
